import {
  useCallback,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react'

import { IconButton } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  ConversationFooterBottomRowStyled,
  ConversationFooterDisclaimerStyled,
  ConversationFooterInputBoxStyled,
  ConversationFooterInputFieldStyled,
  ConversationFooterWrapperStyled,
} from './styles/ConversationFooterStyled'
import { ConversationStreamStatuses } from '../../../../hooks/useConversationStream'
import { useConversation } from '../../../../providers/ConversationProvider'

export const ConversationFooter = () => {
  const intl = useProcurifyIntl()
  const { sendMessage, status } = useConversation()
  const [value, setValue] = useState('')

  const isAwaitingTurn =
    status === ConversationStreamStatuses.STREAMING ||
    status === ConversationStreamStatuses.IDLE
  const isSubmittable = !isAwaitingTurn && value.trim() !== ''

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value)
    },
    []
  )

  const handleSubmit = useCallback(() => {
    if (isAwaitingTurn || value.trim() === '') {
      return
    }
    sendMessage(value)
    setValue('')
  }, [isAwaitingTurn, value, sendMessage])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  return (
    <ConversationFooterWrapperStyled>
      <ConversationFooterInputBoxStyled>
        <ConversationFooterInputFieldStyled
          rows={1}
          aria-label={intl.formatMessage(messages.inputAriaLabel)}
          placeholder={intl.formatMessage(messages.inputPlaceholder)}
          value={value}
          disabled={isAwaitingTurn}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <ConversationFooterBottomRowStyled>
          <IconButton
            aria-label={intl.formatMessage(messages.submitAriaLabel)}
            icon='pointer-up'
            onClick={handleSubmit}
            disabled={!isSubmittable}
          />
        </ConversationFooterBottomRowStyled>
      </ConversationFooterInputBoxStyled>
      <ConversationFooterDisclaimerStyled>
        {intl.formatMessage(messages.disclaimer)}
      </ConversationFooterDisclaimerStyled>
    </ConversationFooterWrapperStyled>
  )
}
